import { useState, useEffect } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { AgentProxyService } from '@/services/agentProxyService'
// TriggersService no longer exists - using stub implementation
import type { TriggerDetailsResponse } from './TriggerDetailsView'
import { useToast } from '@/hooks/useToast'

interface EditTriggerPanelProps {
  connection: DatabaseConnection
  database: string
  triggerName: string
  schema?: string
  initialTrigger?: TriggerDetailsResponse
  onClose: () => void
  onSuccess: () => void
}

export function EditTriggerPanel({ 
  connection, 
  database, 
  triggerName, 
  schema, 
  initialTrigger,
  onClose,
  onSuccess 
}: EditTriggerPanelProps) {
  const [formData, setFormData] = useState({
    name: initialTrigger?.name || triggerName,
    definition: initialTrigger?.definition || '',
    schema: initialTrigger?.schema || schema || 'public',
    tableName: initialTrigger?.table || initialTrigger?.tableName || '',
    timing: initialTrigger?.timing || 'BEFORE',
    event: initialTrigger?.event || 'INSERT'
  })
  const [isSaving, setIsSaving] = useState(false)
  const { showToast } = useToast()

  useEffect(() => {
    if (initialTrigger) {
      setFormData({
        name: initialTrigger.name || triggerName,
        definition: initialTrigger.definition || '',
        schema: initialTrigger.schema || schema || 'public',
        tableName: initialTrigger.table || initialTrigger.tableName || '',
        timing: initialTrigger.timing || 'BEFORE',
        event: initialTrigger.event || 'INSERT'
      })
    }
  }, [initialTrigger, triggerName, schema])

  const handleSave = async () => {
    if (!formData.definition.trim()) {
      showToast('error', 'Trigger definition cannot be empty')
      return
    }

    if (!formData.tableName.trim()) {
      showToast('error', 'Table name is required')
      return
    }

    setIsSaving(true)
    
    try {
      await AgentProxyService.alterTrigger(
        connection.id,
        database,
        triggerName,
        {
          new_trigger_name: formData.name,
          table_name: formData.tableName,
          timing: formData.timing,
          event: formData.event,
          definition: formData.definition,
          schema_name: formData.schema,
          options: {
            drop_and_recreate: true
          }
        }
      )

      showToast('success', 'Trigger updated successfully')
      onSuccess()
      onClose()
    } catch (error: unknown) {
      console.error('Failed to update trigger:', error)
      const message = error instanceof Error ? error.message : 'Unknown error'
      showToast('error', `Failed to update trigger: ${message}`)
    } finally {
      setIsSaving(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <div className="fixed inset-y-0 right-0 w-1/2 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center">
          <div className="text-2xl mr-3">⚡</div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Edit Trigger
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Modify trigger definition (will drop and recreate)
            </p>
          </div>
        </div>
        <button type="button"
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Notice Banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 p-4">
        <div className="flex items-center">
          <svg className="w-5 h-5 text-blue-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
              Drop and Recreate Operation
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Saving changes will drop the existing trigger and create a new one with your modifications.
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          {/* Trigger Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Trigger Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => { handleInputChange('name', e.target.value) }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter trigger name"
            />
          </div>

          {/* Table Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Table Name *
            </label>
            <input
              type="text"
              value={formData.tableName}
              onChange={(e) => { handleInputChange('tableName', e.target.value) }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Table name that the trigger is attached to"
            />
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              The table this trigger is attached to (can be changed)
            </p>
          </div>

          {/* Schema */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Schema
            </label>
            <input
              type="text"
              value={formData.schema}
              onChange={(e) => { handleInputChange('schema', e.target.value) }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter schema name"
            />
          </div>

          {/* Trigger Definition */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Trigger Definition
            </label>
            <textarea
              value={formData.definition}
              onChange={(e) => { handleInputChange('definition', e.target.value) }}
              rows={15}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter trigger definition (SQL)"
            />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Modifying the trigger will drop the existing trigger and recreate it with the new definition.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
        <button type="button"
          onClick={onClose}
          disabled={isSaving}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || !formData.definition.trim() || !formData.tableName.trim()}
          className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <>
              <svg className="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Saving...
            </>
          ) : (
            <>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
              </svg>
              Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  )
}